// tileMesh.ts - one streamed pyramid tile, drawn.
//
// The renderable half of the tile tree (docs/ATLAS-TERRAIN.md, "Tile tree
// contract"): a `LoadedTile`'s height field and albedo become a mesh sitting on
// the body's datum sphere in the Atlas's own frame. The tree owns what is
// resident and what is on screen and knows nothing about three.js; this owns
// geometry, textures and their release, and knows nothing about fetching.
//
// Design: docs/roadmap/03-terrain-pyramid.md, "The runtime". Same-level
// siblings share their edge exactly: an edge vertex's half-sample stencil reads
// the apron sample on one side and the neighbour's own first interior sample on
// the other, and those are the same two numbers in the same order on both
// tiles. Across a LEVEL boundary the two edges cannot match by construction, so
// the tile wears a skirt, and a tile that has just split draws on its parent's
// vertex lattice and morphs off it.
//
// The mesh is COARSER than the height field on purpose, the way DtmPlot's
// lattice is: a drawn tile covers 192 to 384 pixels an edge (the split
// threshold is 1.5 pixels per height sample), so a 256-cell mesh would spend a
// triangle a pixel, and a draw set of a few hundred tiles would be tens of
// millions of triangles against the 2.5 M budget in
// docs/roadmap/07-performance-and-instruments.md. The shape stops chasing
// features the triangles cannot hold; the NORMAL is still taken at the field's
// own sample spacing, so the shading keeps them.

import * as THREE from 'three';
import type { AtmosphereHandle } from './atmosphere';
import { bilinearSample } from './plotField.ts';
import { tileAlbedoUv } from './tileAlbedoUv.ts';
import { GROUND_METALNESS, GROUND_ROUGHNESS } from './groundBrdf.ts';
import {
  TILE_APRON_SAMPLES, TILE_CLAIM_TEXELS, TILE_HEIGHT_SAMPLES, directionForFaceUv,
  directionForLatLon, faceTangent, faceUvForDirection, formatTileKey, gsd, retintClaimTexture,
} from './tileTree.ts';
import type {
  LoadedTile, TileAlbedoRef, TileBounds, TileHeightRef, TileKey, Vec3,
} from './tileTree';
import {
  applyTileArtificialFallback, applyTileArtificialFill, type ArtificialFillRef,
} from './artificialFill.ts';
import { applyTileArtificialRelief, type ArtificialReliefRef } from './artificialRelief.ts';
import { applyBodyShadow, bodyShadowKey, type BodyShadowRef } from './bodyShadow.ts';
import { tileReliefLattice } from './tileRelief.ts';
import { applySeaChunk, SEA_GLSL } from './seaSurface.ts';

/** Quads per tile edge: 2,048 triangles a tile, about 12 px an edge at the split. */
export const TILE_MESH_QUADS = 32;

/**
 * How deep a smooth-ground body's tree goes: the shallowest level whose flat
 * cells sag less than `eyeM` under the sphere they stand in for.
 *
 * A cell's diagonal is a chord, sagging diag^2 / 8R at its middle; past the
 * eye's own height a standing camera would stand that far over the drawn ground.
 * Titan: L4 cells sag 6.1 m, L5 1.5 m, so L5 at the 1.7 m eye.
 */
export function smoothGroundLevel(radiusM: number, eyeM: number): number {
  let level = 0;
  for (; level < 20; level++) {
    const cellM = (Math.PI / 2) * radiusM / (TILE_MESH_QUADS * 2 ** level);
    if ((2 * cellM * cellM) / (8 * radiusM) < eyeM) break;
  }
  return level;
}

/** Samples across one STORED tile edge, apron included. */
const STORED = TILE_HEIGHT_SAMPLES + 2 * TILE_APRON_SAMPLES;

/** Martian dust, for ground no level of the pyramid has imagery for. */
const NO_IMAGERY_COLOR = 0x9a5c3c;

/**
 * The aerial-perspective chunk, shared by every tile material that draws
 * with it.
 *
 * `frameUniforms` is owned by the caller (src/atlas.ts) and updated once a
 * frame - the local-horizon basis and the true sun direction it carries are
 * the same for every tile on screen, so nothing here differs per tile.
 * `atmo.uniforms` and `atmo.glsl` are the shared LUT contract
 * (src/atlas/atmosphere.ts); merging them wholesale is what keeps a tile
 * hill and the sky above it fading toward the same colour.
 */
import { applyCoverageFill } from './coverageFill.ts';
import {
  albedoColorSpace, albedoTransferUniform, applyAlbedoTransfer,
  type AlbedoTransfer, type AlbedoTransferUniform,
} from './albedoTransfer.ts';
import type { CoverageFillRef } from './coverageFill';

export interface TileMeshAtmoRef {
  readonly atmo: AtmosphereHandle;
  readonly frameUniforms: Record<string, THREE.IUniform>;
}

/** Skirt haze distance under `uAtmoSkirtHaze` only; hazed this far, a skirt seen
 *  through a level crack reads as a sky-lit line, not as the ground beside it. */
const ATMO_SKIRT_DISTANCE_M = 200000;

/**
 * The aerial term's declarations and its injection, for a consumer that wears a
 * skirt ring (a tile) or one that does not (Mars's own orrery globe, through
 * `applyGlobeAtmo` in src/atlas.ts).
 *
 * ONE source for both: the globe is what a gap in the streamed pyramid falls
 * back to, so a tile and the globe beside it shade the same skyline and any
 * drift between two copies of this maths shows up exactly there.
 *
 * `uAtmoFrame`/`uAtmoTerrainSunDir` name the per-frame basis and true sun
 * direction `TileMeshAtmoRef.frameUniforms` carries; `atmosphere.ts`'s own
 * chunk does not declare them, since the sky quad builds its own basis
 * per-fragment from the camera's rotation instead. `vAtmoSkirt` is set from the
 * `skirt` attribute below, and `vViewPosition` is the standard material's own
 * varying (camera space, fragment to camera), so the direction and distance
 * cost nothing beyond the one matrix a frame buys everywhere else.
 *
 * `vAtmoSkirt > 0.0` under the control: 1 on the dropped ring and 0 on the edge
 * shared with the tile surface, so all of the curtain but that edge is hazed.
 */
export function atmoTerrainChunk(
  opts: {
    readonly skirt: boolean;
    readonly sphereGround?: boolean;
    /** Draw the body's cloud top in place of its own map while the eye is over
     *  an opaque deck. The globe alone: a tile is never drawn through one. */
    readonly deck?: boolean;
  },
): { readonly decl: string; readonly inject: string; readonly diffuse?: string } {
  const meshDist = opts.skirt
    ? `uAtmoSkirtHaze > 0.5 && vAtmoSkirt > 0.0 ? ${ATMO_SKIRT_DISTANCE_M.toFixed(1)} : length(vViewPosition)`
    : 'length(vViewPosition)';
  // A globe fragment's march distance comes from the RAY and not from the
  // mesh. `orrery.ts` draws the body as a 32x24 sphere, so an interpolated
  // vViewPosition ends on the flat triangle - up to 23.6 km, two Mars scale
  // heights, inside the sphere at a quad's centre - and the march clips to
  // min(distance, shell exit), which drops a quarter of the shell at the
  // sub-point and swings a near-tangent path by 400 km at the limb. That
  // quilted the ORBIT disc on the sphere's own lat/lon grid, at 31-39 px per
  // cell against the 35 px the 11.25 degree columns project to. Perspective
  // correction makes -vViewPosition's DIRECTION the pixel's exact ray, so the
  // ground distance is analytic; only its length was ever the mesh's. Tiles
  // keep the mesh's own distance, which is real relief and whose cells put
  // 0.06 m of sagitta on the same sphere.
  const dist = opts.sphereGround ? 'fxAtmoRayDist' : meshDist;
  // `uAtmoMeshDist` is `descent-dojo --haze --inject quilt`: it puts the globe
  // back on the mesh's own distance, so the control reproduces the artifact
  // without a recompile and the gate has a known answer to fail against.
  // And the sphere is the one the globe is DRAWN on - the pyramid's datum,
  // carried in `uAtmoGlobeRadius` - not `uAtmoRadii.x`, which is the registry's
  // mean radius. Those differ by 7,137 m on Earth, 0.89 Rayleigh scale heights,
  // so a march to `uAtmoRadii.x` ran 7.3 km past the ground the tile beside it
  // stopped at, through the densest air there is, and came back with 2.4x the
  // in-scatter: any tile-cover boundary was a 19 to 60 grey level tone edge
  // with the globe brighter and hazier outside it. Every other body that draws
  // air agrees to under 0.06 of a scale height.
  //
  // The shell's own ground is a FLOOR under that, not an alternative. Over an
  // opaque deck the LUT terminates at the cloud TOP and every sample below it
  // clamps to that density, so marching a Venus globe to the datum 48 km
  // beneath would add three scale heights of the densest air the table has -
  // the white disc `--deck --inject nodeck` exists to catch. The same max
  // covers an unset uniform and the control, which both write 0.
  const pre = opts.sphereGround ? /* glsl */ `
  float fxAtmoMesh = length(vViewPosition);
  float fxAtmoGroundR = max(uAtmoGlobeRadius, uAtmoRadii.x);
  float fxAtmoHit = atmoRaySphere(
    vec3(0.0, max(uAtmoEyeHeight, fxAtmoGroundR), 0.0), fxAtmoDir, fxAtmoGroundR);
  float fxAtmoRayDist = uAtmoMeshDist > 0.5 || fxAtmoHit <= 0.0 ? fxAtmoMesh : fxAtmoHit;
` : '';
  const deckField = opts.deck
    ? {
      diffuse: /* glsl */ `
#include <map_fragment>
if (uAtmoDeckDraw > 0.5) diffuseColor.rgb = mix(diffuseColor.rgb, uAtmoDeckAlbedo, uAtmoLook);
`,
    }
    : {};
  return {
    ...deckField,
    decl: /* glsl */ `
uniform mat3 uAtmoFrame;
uniform vec3 uAtmoTerrainSunDir;
uniform float uAtmoSunIrradiance;
uniform float uAtmoGroundScale;
uniform vec3 uAtmoDeckSkyLight;
uniform float uAtmoFillScale;
uniform float uAtmoAerialOff;
uniform float uAtmoAerialTailOnGround;
uniform float uAtmoLook;
${opts.sphereGround ? 'uniform float uAtmoMeshDist;\nuniform float uAtmoGlobeRadius;' : ''}
${opts.deck ? 'uniform vec3 uAtmoDeckAlbedo;\nuniform float uAtmoDeckDraw;' : ''}
${opts.skirt ? 'varying float vAtmoSkirt;\nuniform float uAtmoSkirtHaze;' : ''}
`,
    inject: /* glsl */ `
// The body shadow's share (bodyShadow.ts) is on the direct light and so on the sunlight the haze scatters.
#ifdef FX_BS_SHARE
#define FX_BS_HAZE fxBsK
#else
#define FX_BS_HAZE 1.0
#endif
{
  vec3 fxAtmoLit = outgoingLight;
  vec3 fxAtmoDir = normalize(uAtmoFrame * (-vViewPosition));
${pre}  float fxAtmoDist = ${dist};
  vec3 fxAtmoT = vec3(1.0);
  vec3 fxAtmoS = vec3(0.0);
  if (uAtmoAerialOff < 0.5) {
    // This fragment's own albedo for the haze's ground-bounce term, not the
    // planet's mean. A local approximation: the march's samples are kilometres
    // away and the ground under them is not the fragment's.
    //
    // This fragment IS ground, so the sky-convergence tail never fires here,
    // whatever the sphere gate would have said about the relief it stands on.
    // descent-dojo --inject aerial-tail-on-ground arms
    // uAtmoAerialTailOnGround to put the sphere gate back, which is the
    // control this fix's leg fails against.
    bool fxEndsOnGround = uAtmoAerialTailOnGround < 0.5;
    atmoAerialPerspective(fxAtmoDir, uAtmoTerrainSunDir, fxAtmoDist,
      diffuseColor.rgb, fxEndsOnGround, fxAtmoT, fxAtmoS);
  }
  // One scale for both terms: three's Lambert puts this ground on the orrery
  // point light's own intensity, and the in-scatter beside it is on the
  // physical solarFluxWm2 times SKY_EXPOSURE the sky is driven from -
  // uAtmoGroundScale in src/atlas.ts is the ratio between the two. The
  // orrery's fake AMBIENT fill is left out of it: it is not an irradiance and
  // has no units to convert, and scaling it lit an atmospheric body's unlit
  // hemisphere by the body's own flux over 320 W/m2.
  vec3 fxAtmoFill = reflectedLight.indirectDiffuse * (1.0 - uAtmoFillScale);
  // Under a deck the far ground has no direct beam to carry (fxAtmoT/fxAtmoS
  // read the same as clear air), so without a term of its own it stays lit by
  // the orrery's fake ambient alone and reads as a black band at the horizon.
  // uAtmoDeckSkyLight is the dome's own radiance: added at the fragment's
  // albedo before the haze, and again as (1-T) times itself so the horizon
  // itself converges on the dome rather than on black as T goes to 0.
  outgoingLight = (((outgoingLight - fxAtmoFill) * uAtmoGroundScale + fxAtmoFill)
      + diffuseColor.rgb * uAtmoDeckSkyLight) * fxAtmoT
    + fxAtmoS * uAtmoSunIrradiance * FX_BS_HAZE + (vec3(1.0) - fxAtmoT) * uAtmoDeckSkyLight;
  // The opaque deck is in FRONT of all of that, and it is the whole frame in
  // the 22 km a Venus descent crosses between the deck's top and its base.
  float fxDeckT;
  vec3 fxDeckS = atmoDeck(vec3(0.0, max(uAtmoEyeHeight, uAtmoRadii.x), 0.0),
    fxAtmoDir, uAtmoTerrainSunDir, fxAtmoDist, fxDeckT);
  outgoingLight = outgoingLight * fxDeckT + fxDeckS * uAtmoSunIrradiance;
  // The orrery's own lit colour at 0, which is the globe with no air mounted.
  outgoingLight = mix(fxAtmoLit, outgoingLight, uAtmoLook);
}
#include <opaque_fragment>
`,
  };
}

const ATMO_TILE_CHUNK = atmoTerrainChunk({ skirt: true });

/**
 * The two ends of the HiRISE tint curve, and the same two `dtmPlot.ts` mixes
 * between: iron oxide at the bright end, dark basaltic sand at the low one.
 *
 * Only reached where a leaf's ground has no ancestor colour to shade at all - a
 * body with no albedo floor. Mars has one at L6, so a Jezero leaf takes the
 * Viking colour and this curve is the walkaround's business.
 */
const ORTHO_TINT = 'vec3(0.185, 0.112, 0.086), vec3(0.615, 0.372, 0.243)';

/**
 * How far a skirt hangs below its tile's own edge.
 *
 * A crack at a level boundary is as deep as the two levels disagree, which is
 * bounded by how much the ground moves over one of the COARSER mesh's cells -
 * so the depth scales with the tile's own relief rather than with a constant.
 * The floor covers a dead-flat tile, where the disagreement is float32 on a
 * 3,400 km radius rather than terrain.
 */
const SKIRT_RELIEF_FRACTION = 0.75;
const SKIRT_FLOOR_SAMPLES = 2;

/**
 * A region image mapped onto one tile, as an affine on the tile's own extent.
 *
 * `uv` is a 2x3 matrix in row-major order over the tile's normalised face
 * extent (a, b), both 0..1: `u = m0 + m1*a + m2*b`, `v = m3 + m4*a + m5*b`. An
 * affine rather than an offset and a scale because a region frame's axes are
 * east and north and a cubesphere face's are neither, so the mapping carries a
 * rotation. `id` is what tells a kept mesh its provider has not changed.
 */
export interface SiteAlbedoRef {
  readonly id: string;
  readonly map: THREE.Texture;
  /**
   * The same image low-passed at the inherited colour product's texel scale.
   *
   * The leaf draws `ortho / lowPass`, so everything finer than the colour comes
   * from the orthoimage and nothing coarser does.
   */
  readonly lowPass: THREE.Texture;
  readonly uv: readonly [number, number, number, number, number, number];
}

export interface TileMeshFrame {
  /** The datum radius the tile's heights are measured from, metres. */
  readonly radiusM: number;
  /** Quads per tile edge. Default `TILE_MESH_QUADS`. */
  readonly quads?: number;
  /** Vertical exaggeration; 1 is true scale. */
  readonly exaggeration?: number;
  /** Albedo anisotropy, from `renderer.capabilities.getMaxAnisotropy()`. */
  readonly anisotropy?: number;
  /** Surface colour where a level carries no imagery. */
  readonly noImageryColor?: THREE.ColorRepresentation;
  /**
   * The ancestor's albedo this tile draws with, from `TileDrawState`.
   *
   * Ignored when the tile carries its own. The sub-rectangle is baked into the
   * uv attribute rather than set on the texture, because one provider's texture
   * is shared by every descendant drawing out of it and `offset`/`repeat` are
   * properties of the texture, not of the draw.
   */
  readonly albedo?: TileAlbedoRef | null;
  /**
   * The ancestor's height field this tile's relief comes from, from `TileDrawState`.
   *
   * Ignored when the tile carries its own. Where a body bakes imagery deeper
   * than relief - Mercury, Venus, Phobos - this is the whole of a leaf's shape:
   * without it every vertex sits on the datum and the ground goes flat under
   * the eye that is closest to it.
   */
  readonly height?: TileHeightRef | null;
  /**
   * A whole-image albedo over this tile, which wins over both of the above.
   *
   * A site bake's leaves are height only, so without this they inherit imagery
   * from the L6 albedo floor at 163 m/texel over ground sampled at 10 m. The
   * region's own orthoimage is finer than any pyramid level, and it is one
   * image over many tiles rather than one texture per tile, so the part of it
   * a tile covers arrives as an affine on the uv attribute.
   */
  readonly siteAlbedo?: SiteAlbedoRef | null;
  /**
   * Read the site orthoimage as luminance detail over the inherited colour.
   *
   * Default true. False is the wholesale replacement it used to be, which is
   * `tile-swap-check.mjs --colour-step --self`'s injected fault.
   */
  readonly orthoTint?: boolean;
  /** Where a shared albedo texture is kept. Required to inherit one. */
  readonly textures?: TileAlbedoCache;
  /** The body's albedo bytes are linear reflectance, not sRGB colour. */
  readonly albedoTransfer?: AlbedoTransfer | null;
  /** Hang a skirt off the tile's edges. Default true. */
  readonly skirts?: boolean;
  /** Carry the geomorph attribute, so `setMorph` does anything. Default true. */
  readonly geomorph?: boolean;
  /** Where the geomorph starts: 0 on the parent's lattice, 1 at full detail. */
  readonly morph?: number;
  /**
   * Lift the tile's own edge ring by this many metres along its radial.
   *
   * A debug injection, not a terrain term. The seam-check's calibration arm
   * needs a level boundary with a step at it and this bake has none - both
   * sides of every boundary on Mars come from one product
   * (docs/roadmap/07-performance-and-instruments.md) - so the arm makes one.
   *
   * The EDGE RING and not the whole tile: lifting the whole patch rigidly moves
   * it out from under the band and leaves a silhouette between two surfaces of
   * near-equal shading, which measured 1.46x its background at 100 m and LESS
   * than its background at 1600 m. A lip on the ring is what a T-junction
   * actually is - the finer tile's edge disagreeing with its coarser
   * neighbour's - and the gradient samples straddle it, so the normals carry
   * the discontinuity too. A sibling boundary stays sealed, since both tiles
   * lift their shared edge by the same amount.
   */
  readonly radialOffsetM?: number;
  /**
   * Which edges carry the lip, as `edgeVerts`'s edge indices in a bitmask.
   *
   * One edge and not the whole ring: the background band is chosen 60 to 250 px
   * perpendicular to the seam, which at 16 km can reach the same tile's
   * opposite edge - and a lipped background reads higher than the seam. 0 means
   * every edge.
   */
  readonly radialOffsetEdges?: number;
  /**
   * The scattering chunk this body carries, or null for `no_atmosphere` /
   * `no_parameters` (docs/roadmap/05-atmosphere-and-light.md).
   *
   * Folded into `customProgramCacheKey` below: a material built with the
   * chunk must never share a program with one built without it.
   */
  readonly atmo?: TileMeshAtmoRef | null;
  /**
   * What an unmeasured imagery texel draws, off the focused body's own row.
   *
   * An albedo tile's alpha IS its coverage (`coverageFill.ts`), and the bake
   * has already put this colour in the RGB under a hole, so the chunk is the
   * identity until a control asks for another colour.
   */
  readonly coverage?: CoverageFillRef | null;
  /**
   * The ancestor's artificial fill this tile draws with, from `TileDrawState`.
   *
   * Ignored when the tile carries its own. Its own chain and its own uv
   * rectangle, because the fill's floor is shallower than the albedo's.
   */
  readonly artificial?: TileAlbedoRef | null;
  /**
   * The ARTIFICIAL FILL switch and its control, shared by every tile of the
   * body, or null when this body publishes no fill.
   *
   * Only the switch is shared: the MAP is per tile and is built here, so the
   * ref this mesh hands the chunk carries the body's `on` and `force` objects
   * and its own texture.
   */
  readonly artificialFill?: ArtificialFillRef | null;
  /**
   * The body's own globe-level fill, for a tile whose ancestor chain streamed
   * none: null on a body that publishes no artificial map at all, and null
   * under `--inject art-globe-fallback-off` so a control can turn this back
   * off.
   *
   * Never used when `artificial` (above) already resolves to a streamed
   * tile - the streamed fill always wins where one was fetched.
   */
  readonly globeArtificial?: ArtificialFillRef | null;
  /**
   * The body's artificial relief, or null on a body whose row publishes none.
   *
   * Always the globe's own map: no pyramid carries a relief role, so there is
   * no per-tile variant of this the way there is for the fill. The switch and
   * the control are the body's, shared with the globe, so a tile and the
   * picture it lands on top of cannot disagree about which way the light
   * falls.
   */
  readonly globeRelief?: ArtificialReliefRef | null;
  /**
   * Procedural heights displacing this tile's vertices, scaled in the shader by
   * `on`. Absent, the tile carries no relief attribute and its program is unchanged.
   */
  readonly tileRelief?: TileReliefField | null;
  /** The body's shadows from its own system, the globe's uniforms; null where nothing can shadow it. */
  readonly bodyShadow?: BodyShadowRef | null;
}

/** A body's procedural heights as a tile mesh reads them: direction is unit body-fixed, z the pole. */
export interface TileReliefField {
  readonly on: { value: number };
  heightM(x: number, y: number, z: number, cellM: number): number;
  /** `tileReliefLattice`'s heights already evaluated, on a worker; absent, `heightM` runs here per lattice vertex. */
  readonly heights?: Float64Array | null;
}

export interface TileMeshHandle {
  readonly object: THREE.Mesh;
  /** Triangles this mesh draws, for the instrument surface. */
  readonly triangles: number;
  /** Whether this tile drew real imagery rather than the no-imagery colour. */
  readonly textured: boolean;
  /** Whose albedo this drew with, or null when it was the tile's own or none. */
  readonly albedoFromId: string | null;
  /** Whose artificial fill this drew with, or null when it was the tile's own or none. */
  readonly artificialFromId: string | null;
  /** Whether this mesh carries an artificial fill at all, own or inherited. */
  readonly artificialTextured: boolean;
  /**
   * Whether this mesh draws the body's globe map as its fill, because no
   * ancestor of it ever streamed one. Counted apart from `artificialTextured`
   * so a reading can tell the two sources of fill apart.
   */
  readonly artificialGlobeTextured: boolean;
  /**
   * Whether this mesh's material shades by the body's artificial relief.
   *
   * Read off the material that was built, not off the switch: the Layers row
   * reports what the frame is drawing, and past the ORBIT edge these meshes
   * are the frame.
   */
  readonly reliefShaded: boolean;
  /** Whose height field this drew with, or null when it was the tile's own or none. */
  readonly heightFromId: string | null;
  /** The relief this mesh's own vertices carry, metres, exaggeration excluded. */
  readonly heightRangeM: number;
  /** Whether a skirt was built, so the seam instrument can say which arm it is in. */
  readonly skirted: boolean;
  /** Whether this mesh carries `tileRelief` displacement attributes. */
  readonly reliefDisplaced: boolean;
  /** Milliseconds this thread spent on `tileRelief` for this tile (the heights too when none were given), 0 without it. */
  readonly reliefEvalMs: number;
  /** The lowest and highest `tileRelief` height on this tile's vertices, metres, exaggeration included; 0 without it. */
  readonly reliefLoM: number;
  readonly reliefHiM: number;
  /** 0 on the parent's vertex lattice, 1 at full detail. A no-op without `geomorph`. */
  setMorph(t: number): void;
  /**
   * The radius the DRAWN surface stands at under a body-fixed direction, metres.
   *
   * The flat triangle the vertex buffer and the geomorph as it stands right now
   * draw there, so it is the surface on screen rather than the height field
   * under it: a tile part way
   * to its parent's lattice draws neither field's own value. NaN off this
   * tile's own face or footprint.
   */
  drawnRadiusAt(dir: Vec3): number;
  /**
   * Refill this tile's claim texture from the claimed set as it stands now.
   *
   * The texture is baked at decode, so without this a plot staked while this
   * tile is drawn does not tint its ground until an altitude change mounts a
   * new leaf. Returns whether a byte moved, so a caller can tell a real
   * retint from a pass over an unchanged set.
   */
  retintClaims(claimedAt: (res: number) => ReadonlySet<string>): boolean;
  /**
   * The two ends of one edge, in the mesh's own local space, in
   * `neighboursOf` order: -u, +u, -v, +v. What the seam-check points at.
   */
  edgeEnds(edge: number): readonly [THREE.Vector3, THREE.Vector3];
  /**
   * Every vertex along one edge, in the mesh's own local space, ordered along
   * the other axis. Two same-level neighbours' shared edge must be the same
   * points, and this is what says whether it is.
   */
  edgeVerts(edge: number): Float32Array;
  /**
   * Upload this mesh's own textures now, off the frame it is first drawn on.
   *
   * A split reveals a whole replacement set on one frame (`resolveTileCover`
   * in src/atlas.ts), and three uploads a texture on the first draw that
   * samples it. The build is already budgeted per frame, so the upload is
   * charged there instead of to the reveal.
   */
  warm(renderer: THREE.WebGLRenderer): void;
  dispose(): void;
}

/**
 * One `THREE.Texture` per albedo tile, however many meshes draw out of it.
 *
 * Below the albedo floor a whole subtree inherits one ancestor's imagery - 4
 * tiles at one level down, 256 at four - and a texture per mesh would upload
 * the same megabyte that many times. The ImageBitmap still belongs to the
 * `LoadedTile`, so this owns the GL texture and nothing else; the tree pins a
 * provider while any of its descendants is drawn, which is what makes a
 * borrowed bitmap safe to hold.
 */
/** Which of a tile's two images a cache entry holds. */
export type TileImageRole = 'albedo' | 'artificial';

/** The cache's own key for one tile's image, which is also what `release` takes. */
export function tileImageId(key: TileKey, role: TileImageRole): string {
  return role === 'albedo' ? formatTileKey(key) : `${formatTileKey(key)}#art`;
}

export interface TileAlbedoCache {
  acquire(tile: LoadedTile, anisotropy: number, role?: TileImageRole,
    transfer?: AlbedoTransfer | null): THREE.Texture | null;
  release(id: string): void;
  /** Live textures, for the leak check. */
  readonly size: number;
  /** Textures created since the cache opened: an upload each, on the next draw. */
  readonly created: number;
  dispose(): void;
}

export function createTileAlbedoCache(): TileAlbedoCache {
  const entries = new Map<string, { texture: THREE.Texture; refs: number }>();
  let created = 0;
  return {
    acquire(tile: LoadedTile, anisotropy: number, role: TileImageRole = 'albedo',
      transfer: AlbedoTransfer | null = null): THREE.Texture | null {
      const id = tileImageId(tile.key, role);
      const hit = entries.get(id);
      if (hit) {
        hit.refs++;
        return hit.texture;
      }
      const source = (role === 'albedo' ? tile.albedo : tile.artificial)?.source;
      if (source === undefined || typeof ImageBitmap === 'undefined'
        || !(source instanceof ImageBitmap)) return null;
      const texture = new THREE.Texture(source);
      // Named because `descent-dojo --colour` classifies a texture by its name
      // before it looks at its owner, and imagery has to read sRGB. The fill's
      // own name says it is not measured ground, and it reads sRGB too: it is
      // written in the albedo role's own colour space, by design.
      texture.colorSpace = role === 'albedo' ? albedoColorSpace(transfer) : THREE.SRGBColorSpace;
      texture.name = role === 'albedo' ? 'atlas-tile-albedo' : 'atlas-tile-artificial';
      // The unpack flip does not apply to an ImageBitmap source, so asking for
      // it would be a flag that does nothing: `decodeAlbedo` hands the rows
      // over as the tile stores them and the uv rectangles below read them
      // that way round.
      texture.flipY = false;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
      texture.anisotropy = Math.max(1, anisotropy);
      texture.needsUpdate = true;
      entries.set(id, { texture, refs: 1 });
      created++;
      return texture;
    },
    release(id: string): void {
      const hit = entries.get(id);
      if (!hit) return;
      hit.refs--;
      if (hit.refs > 0) return;
      entries.delete(id);
      hit.texture.dispose();
    },
    get size(): number { return entries.size; },
    get created(): number { return created; },
    dispose(): void {
      for (const entry of entries.values()) entry.texture.dispose();
      entries.clear();
    },
  };
}

/**
 * Body-fixed to the Atlas's own axes.
 *
 * `tileTree` addresses the cube in a z-north frame with +x through longitude 0
 * (docs/ATLAS-TERRAIN.md, "Addressing"); every lat/lon in the Atlas is y-north
 * with longitude east toward -z (`latLonToVec3` in atlas.ts). One swap, in one
 * place, so a tile and a hex outline cannot disagree about where north is.
 */
export function bodyToAtlas(x: number, y: number, z: number, out: THREE.Vector3): THREE.Vector3 {
  return out.set(x, z, -y);
}

/** The same swap the other way, which is what builds a `View`'s `cameraM`. */
export function atlasToBody(v: THREE.Vector3): [number, number, number] {
  return [v.x, -v.z, v.y];
}

/** `drawnRadiusAt`'s triangle, three body-fixed vertices, reused across calls. */
const drawnTri = new Float64Array(9);

// All tiles have the same topology, so the index is built once per winding and
// shared. Three deletes a shared index's GL buffer when any one geometry using
// it is disposed and re-uploads it on the next draw of another, which is 24 KB
// of churn per unmount against one copy per resident tile.
const indexCache = new Map<string, THREE.BufferAttribute>();

function sharedIndex(quads: number, flipped: boolean, skirt: boolean): THREE.BufferAttribute {
  const cacheKey = `${quads}:${flipped ? 1 : 0}:${skirt ? 1 : 0}`;
  const hit = indexCache.get(cacheKey);
  if (hit) return hit;
  const verts = quads + 1;
  const ring = perimeter(quads);
  const idx = new Uint16Array(quads * quads * 6 + (skirt ? ring.length * 6 : 0));
  let k = 0;
  const tri = (a: number, b: number, c: number): void => {
    if (flipped) { idx[k++] = a; idx[k++] = c; idx[k++] = b; } else { idx[k++] = a; idx[k++] = b; idx[k++] = c; }
  };
  for (let j = 0; j < quads; j++) {
    for (let i = 0; i < quads; i++) {
      const a = j * verts + i;
      tri(a, a + 1, a + verts);
      tri(a + 1, a + verts + 1, a + verts);
    }
  }
  if (skirt) {
    // The perimeter runs bottom, right, top, left, and outward is always a
    // quarter turn clockwise from the travel direction seen from outside - so
    // one winding rule covers all four edges and the wrap.
    const base = verts * verts;
    for (let r = 0; r < ring.length; r++) {
      const next = (r + 1) % ring.length;
      const p0 = ring[r]!;
      const p1 = ring[next]!;
      tri(p0, base + r, base + next);
      tri(p0, base + next, p1);
    }
  }
  const attr = new THREE.BufferAttribute(idx, 1);
  indexCache.set(cacheKey, attr);
  return attr;
}

/**
 * The vertex grid's edge, once round, as grid indices.
 *
 * Bottom row along +u, right column along +v, top row back along -u, left
 * column back along -v: 4*quads entries with each corner counted once, which is
 * what makes the skirt a closed loop with no seam of its own.
 */
function perimeter(quads: number): Int32Array {
  const verts = quads + 1;
  const out = new Int32Array(4 * quads);
  let k = 0;
  for (let i = 0; i < quads; i++) out[k++] = i;
  for (let j = 0; j < quads; j++) out[k++] = j * verts + quads;
  for (let i = quads; i > 0; i--) out[k++] = quads * verts + i;
  for (let j = quads; j > 0; j--) out[k++] = j * verts;
  return out;
}

// Whether du x dv points INTO the sphere on a face, which is what decides the
// winding. It is three of the six, because the equal-angle projection takes u
// and v to the face's other two axes in ascending order and the middle pair is
// therefore swapped relative to the other two. Measured off the projection
// itself rather than tabulated, so the two cannot drift apart.
const windingByFace: Array<boolean | undefined> = [];

function windingFlipped(face: number): boolean {
  const known = windingByFace[face];
  if (known !== undefined) return known;
  const d = 1e-4;
  const p = directionForFaceUv(face, 0.5, 0.5);
  const pu = directionForFaceUv(face, 0.5 + d, 0.5);
  const pv = directionForFaceUv(face, 0.5, 0.5 + d);
  const ax = pu[0] - p[0];
  const ay = pu[1] - p[1];
  const az = pu[2] - p[2];
  const bx = pv[0] - p[0];
  const by = pv[1] - p[1];
  const bz = pv[2] - p[2];
  const nx = ay * bz - az * by;
  const ny = az * bx - ax * bz;
  const nz = ax * by - ay * bx;
  const flipped = nx * p[0] + ny * p[1] + nz * p[2] < 0;
  windingByFace[face] = flipped;
  return flipped;
}

interface AxisTable {
  /** Stored-sample coordinate at each vertex. */
  readonly s: Float64Array;
  /** `tan` of the face angle at each vertex, and half a sample either side. */
  readonly tan: Float64Array;
  readonly tanLo: Float64Array;
  readonly tanHi: Float64Array;
}

/**
 * The constant term mapping a leaf's sample coordinate into its ancestor's.
 *
 * Sample centres are half a sample inside the tile's own angular edge, so the
 * fraction across a tile is (s - apron + 0.5) / samples on both sides and the
 * mapping is s * scale + this. Zero when nothing is inherited, which is what
 * keeps a tile at or above the height floor building exactly the field it
 * built before.
 */
function sampleBias(offset: number, scale: number): number {
  return offset * TILE_HEIGHT_SAMPLES + (1 - scale) * (TILE_APRON_SAMPLES - 0.5);
}

/**
 * The height the MESH draws under a body-fixed lat/lon, metres above the datum.
 *
 * `leafHeightAtLatLon` reads the stored field, 256 samples across a tile edge.
 * The mesh above is 33 vertices across the same edge and its triangles are flat
 * between them, so the two disagree by whatever relief lives inside one mesh
 * quad: 12.7 m on Ceres at its own pyramid floor, and either sign. Anything
 * that has to put a camera ON the ground reads this instead, because the field
 * is not what is drawn. Same lattice `axisTable` builds, the same ancestor
 * resampling `sampleH` does, and the quad's own diagonal, which `sharedIndex`
 * runs from (i+1, j) to (i, j+1). The one term left out is the chord sagitta of
 * a flat facet under the sphere through its corners - 0.25 m at Phobos's L2,
 * and always upward, so the reading errs to the safe side of the ground.
 */
export function meshHeightAtLatLon(
  bounds: TileBounds, own: LoadedTile, inherit: TileHeightRef | null,
  latRad: number, lonRad: number, quads = TILE_MESH_QUADS,
): number {
  const use = own.height === null ? inherit : null;
  const field = own.height ?? use?.from.height ?? null;
  if (field === null) return NaN;
  const f = faceUvForDirection(directionForLatLon(latRad, lonRad));
  if (f.face !== bounds.face) return NaN;
  const hScale = use ? use.scale : 1;
  const hBiasU = use ? sampleBias(use.offsetU, use.scale) : 0;
  const hBiasV = use ? sampleBias(use.offsetV, use.scale) : 0;
  const at = (i: number, j: number): number => bilinearSample(field, STORED, STORED,
    hScale * ((i / quads) * TILE_HEIGHT_SAMPLES + TILE_APRON_SAMPLES - 0.5) + hBiasU,
    hScale * ((j / quads) * TILE_HEIGHT_SAMPLES + TILE_APRON_SAMPLES - 0.5) + hBiasV);
  const gu = ((f.u - bounds.u0) / (bounds.u1 - bounds.u0)) * quads;
  const gv = ((f.v - bounds.v0) / (bounds.v1 - bounds.v0)) * quads;
  const i0 = Math.min(quads - 1, Math.max(0, Math.floor(gu)));
  const j0 = Math.min(quads - 1, Math.max(0, Math.floor(gv)));
  const a = gu - i0;
  const b = gv - j0;
  return a + b <= 1
    ? at(i0, j0) + (at(i0 + 1, j0) - at(i0, j0)) * a + (at(i0, j0 + 1) - at(i0, j0)) * b
    : at(i0 + 1, j0 + 1) + (at(i0, j0 + 1) - at(i0 + 1, j0 + 1)) * (1 - a)
      + (at(i0 + 1, j0) - at(i0 + 1, j0 + 1)) * (1 - b);
}

/**
 * The ancestor field's OWN range over the footprint a leaf borrows, metres.
 *
 * The floor `surface-clamp --relief` gates the leaf's mesh relief against: a
 * mesh is 33 vertices across a footprint the ancestor stores up to 128 samples
 * of, so it cannot carry more range than this and reads a fraction of it.
 */
export function inheritedHeightRangeM(ref: TileHeightRef): number {
  const field = ref.from.height;
  if (!field) return 0;
  const edge = TILE_APRON_SAMPLES - 0.5;
  const u0 = ref.offsetU * TILE_HEIGHT_SAMPLES + edge;
  const v0 = ref.offsetV * TILE_HEIGHT_SAMPLES + edge;
  const u1 = u0 + ref.scale * TILE_HEIGHT_SAMPLES;
  const v1 = v0 + ref.scale * TILE_HEIGHT_SAMPLES;
  const iu0 = Math.max(0, Math.floor(u0));
  const iu1 = Math.min(STORED - 1, Math.ceil(u1));
  const iv0 = Math.max(0, Math.floor(v0));
  const iv1 = Math.min(STORED - 1, Math.ceil(v1));
  let lo = Infinity;
  let hi = -Infinity;
  for (let j = iv0; j <= iv1; j++) {
    for (let i = iu0; i <= iu1; i++) {
      const h = field[j * STORED + i]!;
      if (h < lo) lo = h;
      if (h > hi) hi = h;
    }
  }
  return Number.isFinite(hi - lo) ? +(hi - lo).toFixed(3) : 0;
}

/**
 * One axis of the vertex grid, tabulated.
 *
 * The equal-angle face angle is separable - the tangent along u depends on u
 * alone - and the normal's stencil wants each vertex half a sample either side
 * of itself, so a row of tangents is three `tan` calls per vertex rather than
 * five per vertex times the whole grid.
 *
 * Sample centres: the tile's angular edge falls half a sample outside its first
 * interior sample, which is where the apron's weight at the boundary comes
 * from, and it is why a half-sample stencil at an edge vertex reads exactly the
 * two samples the neighbouring tile reads there.
 */
function axisTable(t0: number, t1: number, quads: number): AxisTable {
  const n = quads + 1;
  const s = new Float64Array(n);
  const tan = new Float64Array(n);
  const tanLo = new Float64Array(n);
  const tanHi = new Float64Array(n);
  const span = t1 - t0;
  const perSample = span / TILE_HEIGHT_SAMPLES;
  for (let i = 0; i < n; i++) {
    const f = i / quads;
    const t = t0 + span * f;
    s[i] = f * TILE_HEIGHT_SAMPLES + TILE_APRON_SAMPLES - 0.5;
    tan[i] = faceTangent(t);
    tanLo[i] = faceTangent(t - perSample * 0.5);
    tanHi[i] = faceTangent(t + perSample * 0.5);
  }
  return { s, tan, tanLo, tanHi };
}

/**
 * Injects the morph and/or atmosphere chunks into a tile material, and gives
 * it the matching program cache key.
 *
 * Shared between `createTileMesh` and `warmAtmoTileProgram` below so the two
 * can never drift apart: a warmed key that does not byte-for-byte match a
 * real tile's compiles a SECOND program instead of reusing the first, which
 * defeats the warm-up.
 */
interface TileOrthoRef {
  readonly map: { value: THREE.Texture | null };
  readonly lowPass: { value: THREE.Texture | null };
  /** No ancestor colour under this leaf, so the ortho IS the albedo. */
  readonly curve: boolean;
}

/**
 * The claim wash's own multiply, as GLSL.
 *
 * Shared with the focused globe's chunk in src/atlas.ts, which carries the same
 * wash above the ORBIT band edge where there is no tile to carry it: one number
 * for both, so a reading taken at ORBIT and one taken at LOCAL are the same
 * measurement.
 */
export const CLAIM_TINT_GLSL = 'vec3(1.28, 1.10, 0.62)';

/** The claim wash's own texture, this tile's own - never an ancestor's. */
interface TileClaimRef {
  readonly map: { value: THREE.Texture | null };
}

/** The globe's own fill, standing in on a tile with none of its own streamed. */
interface TileArtificialGlobeRef {
  readonly ref: ArtificialFillRef;
  readonly origin: THREE.Vector3;
}

/** The globe's own relief, shading a tile of the same body. */
interface TileReliefRef {
  readonly ref: ArtificialReliefRef;
  readonly origin: THREE.Vector3;
}

function applyTileChunks(
  material: THREE.MeshStandardMaterial, morphU: { value: number }, hasMorph: boolean,
  atmoRef: TileMeshAtmoRef | null, ortho: TileOrthoRef | null, claim: TileClaimRef | null,
  coverage: CoverageFillRef | null = null, artificial: ArtificialFillRef | null = null,
  globeArtificial: TileArtificialGlobeRef | null = null,
  globeRelief: TileReliefRef | null = null,
  tileRelief: { readonly on: { value: number } } | null = null,
  albedoDn: AlbedoTransferUniform | null = null,
  bodyShadow: { readonly ref: BodyShadowRef; readonly origin: THREE.Vector3 } | null = null,
): void {
  if (!hasMorph && !atmoRef && !ortho && !claim && !coverage && !artificial
    && !globeArtificial && !globeRelief && !tileRelief && !albedoDn && !bodyShadow) return;
  material.onBeforeCompile = (shader): void => {
    if (bodyShadow) applyBodyShadow(shader, bodyShadow.ref, bodyShadow.origin);
    // Ahead of the morph block so its line lands after the morph's own.
    if (tileRelief) {
      shader.uniforms['tileReliefOn'] = tileRelief.on;
      const morphTerm = hasMorph ? ' + reliefMorph * (1.0 - fxMorph)' : '';
      shader.vertexShader = 'uniform float tileReliefOn;\nattribute vec3 reliefDelta;\n'
        + `${hasMorph ? 'attribute vec3 reliefMorph;\n' : ''}attribute vec3 reliefNormal;\n${shader.vertexShader}`
          .replace('#include <beginnormal_vertex>', '#include <beginnormal_vertex>\n'
            + '  if (tileReliefOn > 0.0) objectNormal = normalize(mix(objectNormal, reliefNormal, tileReliefOn));')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\n'
            + `  if (tileReliefOn > 0.0) transformed += tileReliefOn * (reliefDelta${morphTerm});`);
    }
    if (hasMorph) {
      shader.uniforms['fxMorph'] = morphU;
      shader.vertexShader = `uniform float fxMorph;\nattribute vec3 morphDelta;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>',
          '#include <begin_vertex>\n  transformed += morphDelta * (1.0 - fxMorph);');
    }
    if (ortho) {
      shader.uniforms['fxOrtho'] = ortho.map;
      shader.uniforms['fxOrthoLow'] = ortho.lowPass;
      shader.vertexShader = `attribute vec2 uvOrtho;\nvarying vec2 vOrthoUv;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vOrthoUv = uvOrtho;');
      // A RATIO against a low-pass of the same image, so the inherited colour
      // survives and only the detail finer than it comes from here. The clamp
      // is the tail of a contrast-stretched product, not a physical bound.
      // The curve branch is the other case: no ancestor colour to shade, so
      // the orthoimage is the whole albedo and takes the plot's own tint,
      // undoing the sampler's sRGB decode because that curve reads the
      // product's own bytes.
      // On `diffuseColor` and not on `outgoingLight`: the detail belongs to the
      // albedo the lighting then lights, so the aerial term below composes as
      // lit(albedo * ortho / lowpass) rather than scaling a lit colour.
      const detail = ortho.curve
        ? '  float fxO = pow(clamp(texture2D(fxOrtho, vOrthoUv).r, 0.0, 1.0), 0.4545);\n'
          + `  diffuseColor.rgb *= mix(${ORTHO_TINT}, fxO);`
        : '  float fxLow = max(texture2D(fxOrthoLow, vOrthoUv).r, 1e-3);\n'
          + '  diffuseColor.rgb *= clamp(texture2D(fxOrtho, vOrthoUv).r / fxLow, 0.0, 4.0);';
      shader.fragmentShader = 'uniform sampler2D fxOrtho;\nuniform sampler2D fxOrthoLow;\n'
        + `varying vec2 vOrthoUv;\n${shader.fragmentShader}`
          .replace('#include <map_fragment>', `#include <map_fragment>\n${detail}`);
    }
    if (atmoRef) {
      // The same uniform objects atlas.ts owns, not copies: a value it sets
      // this frame is what every tile material reads without being touched
      // again.
      Object.assign(shader.uniforms, atmoRef.atmo.uniforms, atmoRef.frameUniforms);
      shader.vertexShader = `attribute float skirt;\nvarying float vAtmoSkirt;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vAtmoSkirt = skirt;');
      shader.fragmentShader = `${atmoRef.atmo.glsl}\n${ATMO_TILE_CHUNK.decl}\n${SEA_GLSL}\n${shader.fragmentShader}`
        .replace('#include <opaque_fragment>', ATMO_TILE_CHUNK.inject);
      applySeaChunk(shader);
    }
    if (claim) {
      // Own dedicated attribute and varying, not `vUv`: `uv` points into an
      // ancestor's sub-rectangle when this tile inherits its colour, and the
      // claim texture never does. After the aerial term, so a wash reads
      // through the same haze the ground under it does.
      shader.uniforms['fxClaims'] = claim.map;
      shader.vertexShader = `attribute vec2 uvClaim;\nvarying vec2 vClaimUv;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vClaimUv = uvClaim;');
      shader.fragmentShader = `uniform sampler2D fxClaims;\nvarying vec2 vClaimUv;\n${shader.fragmentShader}`
        .replace('#include <opaque_fragment>',
          '  float fxClaimed = texture2D(fxClaims, vClaimUv).r;\n'
          + `  outgoingLight *= mix(vec3(1.0), ${CLAIM_TINT_GLSL}, fxClaimed);\n`
          + '#include <opaque_fragment>');
    }
    // Last two, so their substitutions sit straight after the sampled texel
    // and ahead of the ortho detail that multiplies what they leave. The fill
    // goes in after coverage, which puts it BEFORE coverage in the drawn
    // order: coverage keys on the measured alpha and must still be able to
    // paint over invented ground.
    if (coverage) applyCoverageFill(shader, coverage);
    // The streamed fill wins where one was fetched; the globe fallback is
    // only ever built for a tile where `artificial` resolved to null.
    if (artificial) applyTileArtificialFill(shader, artificial);
    else if (globeArtificial) applyTileArtificialFallback(shader, globeArtificial.ref, globeArtificial.origin);
    // On the NORMAL rather than on the colour, so it lands in its own
    // substitution and composes with whichever fill above it drew.
    if (globeRelief) applyTileArtificialRelief(shader, globeRelief.ref, globeRelief.origin);
    if (albedoDn) applyAlbedoTransfer(shader, albedoDn);
  };
  // Two programs, not one per tile: without this every material with its own
  // onBeforeCompile compiles its own copy of the shader. The chunk is its own
  // axis of the key - a body with no atmosphere must never share a program
  // with one that carries the aerial term.
  material.customProgramCacheKey = (): string =>
    `fxciv-tile-${hasMorph ? 'm1' : 'm0'}${atmoRef ? `-a1${atmoRef.atmo.glslKey}` : '-a0'}`
    + `-o${ortho ? (ortho.curve ? 'c' : 'd') : '0'}${claim ? '-c1' : '-c0'}${coverage ? '-cov1' : ''}`
    + `${artificial ? '-art1' : globeArtificial ? '-artg1' : ''}${globeRelief ? '-rel1' : ''}`
    + `${albedoDn ? '-adn1' : ''}${tileRelief ? '-prel1' : ''}${bodyShadowKey(bodyShadow?.ref ?? null)}`;
}

/**
 * Every tile program the descent will draw with, linked before the descent.
 *
 * Without this the first tile of each variant pays its own compile inside the
 * band it arrives in - a link plus three's own uniform enumeration for it is
 * 40 to 80 ms on the calling thread, which is two vsync frames of a 50 ms
 * budget, and no amount of staging divides one link. `applyTileChunks` above
 * is shared with `createTileMesh` so a warmed key cannot drift from a real
 * tile's: a key that differs by one character compiles a SECOND program
 * instead of reusing the first.
 *
 * The eight variants are the whole product a real tile can ask for. Ortho
 * detail and the ortho CURVE are the two site branches (`orthoCurve` is
 * `orthoDetail && map === null`, so the curve never carries a map and detail
 * always does); the claim wash is its own axis, present on a tile whose quad
 * holds a cell and absent on one that does not; and imagery is the third,
 * since a tile with no picture is built on `color` and differs in USE_MAP and
 * USE_UV. Only the morph variant of each: `TILE_MESH_QUADS` is even and
 * `tileDebug.geomorph` defaults on, so `m1` is what every real tile mounts
 * with, and `m0` is the seam-check calibration's own debug path.
 *
 * The materials stay ALIVE for the session, which is why this returns a
 * handle: three's `dispose` releases a material's program references and
 * dropping the last one destroys the linked program, so warming and freeing
 * in the same tick pays for a link and then deletes it. Nothing here is
 * compiled or drawn - the caller does that from inside its own draw bracket,
 * where the renderer state that also keys a program is the state the descent
 * draws in.
 */
export interface WarmProgramHandle {
  /** Added to the scene by the caller, so `renderer.compile` can walk it. */
  readonly group: THREE.Group;
  dispose(): void;
}

export function warmTilePrograms(
  atmoRef: TileMeshAtmoRef | null, coverage: CoverageFillRef | null,
  artificial: ArtificialFillRef | null = null, globeArtificial: ArtificialFillRef | null = null,
  globeRelief: ArtificialReliefRef | null = null,
  tileReliefOn: { value: number } | null = null,
  bodyShadow: BodyShadowRef | null = null,
): WarmProgramHandle {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(6), 2));
  geometry.setAttribute('uvOrtho', new THREE.BufferAttribute(new Float32Array(6), 2));
  geometry.setAttribute('uvClaim', new THREE.BufferAttribute(new Float32Array(6), 2));
  geometry.setAttribute('uvArt', new THREE.BufferAttribute(new Float32Array(6), 2));
  geometry.setAttribute('skirt', new THREE.BufferAttribute(new Float32Array(3), 1));
  geometry.setAttribute('morphDelta', new THREE.BufferAttribute(new Float32Array(9), 3));
  for (const name of ['reliefDelta', 'reliefMorph', 'reliefNormal']) {
    geometry.setAttribute(name, new THREE.BufferAttribute(new Float32Array(9), 3));
  }
  const map = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1);
  // `descent-dojo --colour`'s role table reads a texture's name: this one is
  // imagery standing in for a tile's albedo, so it carries both the prefix and
  // the colour space a real one does.
  map.name = 'tile-albedo:program-warm';
  map.colorSpace = THREE.SRGBColorSpace;
  map.needsUpdate = true;
  const orthoU = { value: map as THREE.Texture | null };
  const claimU = { value: null as THREE.Texture | null };
  const group = new THREE.Group();
  group.name = 'tile-program-warm';
  const materials: THREE.MeshStandardMaterial[] = [];
  const variants: Array<{
    ortho: 'none' | 'detail' | 'curve'; mapped: boolean; claim: boolean;
    art: 'none' | 'tile' | 'globe';
  }> = [];
  for (const claim of [false, true]) {
    for (const mapped of [true, false]) variants.push({ ortho: 'none', mapped, claim, art: 'none' });
    variants.push({ ortho: 'detail', mapped: true, claim, art: 'none' });
    variants.push({ ortho: 'curve', mapped: false, claim, art: 'none' });
    // The fill's own axis, and only over the shape a filled tile actually has:
    // a global leaf with its own imagery and no region under it. Warming the
    // whole cross product would link eight programs no descent asks for.
    if (artificial) variants.push({ ortho: 'none', mapped: true, claim, art: 'tile' });
    // The globe-fallback's own axis: a leaf whose ancestor chain never
    // streamed a fill, standing in with the body's own globe map instead.
    if (globeArtificial) variants.push({ ortho: 'none', mapped: true, claim, art: 'globe' });
  }
  // One map value for every warm material: the switch and the control are the
  // body's, the texture is a stand-in for whichever tile's fill lands.
  const artU = { value: map as THREE.Texture | null };
  const albedoDnU = albedoTransferUniform(null, false);
  const warmOrigin = new THREE.Vector3(0, 0, 0);
  for (const v of variants) {
    const material = new THREE.MeshStandardMaterial({
      roughness: GROUND_ROUGHNESS,
      metalness: GROUND_METALNESS,
      ...(v.mapped ? { map } : { color: new THREE.Color(NO_IMAGERY_COLOR) }),
    });
    material.name = `tile-warm-${v.ortho}${v.mapped ? '-map' : ''}${v.claim ? '-claim' : ''}`
      + `${v.art !== 'none' ? `-art-${v.art}` : ''}`;
    applyTileChunks(material, { value: 1 }, true, atmoRef,
      v.ortho === 'none' ? null : { map: orthoU, lowPass: orthoU, curve: v.ortho === 'curve' },
      v.claim ? { map: claimU } : null, coverage,
      v.art === 'tile' && artificial ? { map: artU, on: artificial.on, force: artificial.force, dn: artificial.dn } : null,
      v.art === 'globe' && globeArtificial ? { ref: globeArtificial, origin: warmOrigin } : null,
      // Not an axis of its own: the relief is the BODY's, so every tile of a
      // body that publishes one carries the chunk and every one of a body that
      // does not carries none.
      globeRelief ? { ref: globeRelief, origin: warmOrigin } : null,
      tileReliefOn ? { on: tileReliefOn } : null, albedoDnU,
      bodyShadow ? { ref: bodyShadow, origin: warmOrigin } : null);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    group.add(mesh);
    materials.push(material);
  }
  return {
    group,
    dispose(): void {
      for (const m of materials) m.dispose();
      geometry.dispose();
      map.dispose();
    },
  };
}

export function createTileMesh(tile: LoadedTile, frame: TileMeshFrame): TileMeshHandle {
  const quads = Math.max(1, Math.floor(frame.quads ?? TILE_MESH_QUADS));
  const verts = quads + 1;
  const skirts = frame.skirts !== false;
  // The parent's vertices land on this tile's EVEN indices, which only exists
  // as a lattice when the count is even.
  const geomorph = frame.geomorph !== false && quads % 2 === 0;
  const exag = frame.exaggeration ?? 1;
  const radiusM = frame.radiusM;
  const rOff = frame.radialOffsetM ?? 0;
  const rOffEdges = frame.radialOffsetEdges ?? 0;
  const bounds = tile.bounds;
  // A leaf under the height floor has no field of its own, so its relief is
  // the nearest resident ancestor's, resampled over the footprint this tile
  // covers. Sample coordinates are affine between the two: a leaf coordinate
  // su sits at (su - apron + 0.5) / samples across its own tile, and the same
  // fraction of the sub-rectangle inside the ancestor's.
  const inheritH = tile.height === null ? frame.height ?? null : null;
  const height = tile.height ?? inheritH?.from.height ?? null;
  if (height !== null && height.length !== STORED * STORED) {
    throw new Error(`tile ${formatTileKey(tile.key)}: ${height.length} height samples, `
      + `expected ${STORED * STORED}`);
  }
  const hScale = inheritH ? inheritH.scale : 1;
  const hBiasU = inheritH ? sampleBias(inheritH.offsetU, inheritH.scale) : 0;
  const hBiasV = inheritH ? sampleBias(inheritH.offsetV, inheritH.scale) : 0;
  const face = bounds.face;
  const axis = face % 2 === 0 ? 1 : -1;
  const tabU = axisTable(bounds.u0, bounds.u1, quads);
  const tabV = axisTable(bounds.v0, bounds.v1, quads);

  const sampleH = (su: number, sv: number): number => (height === null ? 0
    : bilinearSample(height, STORED, STORED, hScale * su + hBiasU, hScale * sv + hBiasV));

  // Five points a vertex: the vertex itself, then half a sample along each axis
  // either side of it, which is the gradient the normal comes from.
  const pt = new Float64Array(15);
  const put = (k: number, a: number, c: number, h: number, off = 0): void => {
    // The same permutation and normalise `directionForFaceUv` does, with the
    // tangents already in hand. Checked against it below.
    let x: number;
    let y: number;
    let z: number;
    if (face < 2) { x = axis; y = a; z = c; } else if (face < 4) { x = a; y = axis; z = c; } else { x = a; y = c; z = axis; }
    const r = (radiusM + h * exag + off) / Math.hypot(x, y, z);
    pt[k] = x * r;
    pt[k + 1] = y * r;
    pt[k + 2] = z * r;
  };

  // Positions are relative to the tile's own centre, and the mesh carries the
  // centre: 3.4e6 m of radius in a float32 attribute quantises to a quarter of
  // a metre, while the same offsets about a tile centre hold millimetres.
  const hCentre = sampleH(
    TILE_HEIGHT_SAMPLES / 2 + TILE_APRON_SAMPLES - 0.5,
    TILE_HEIGHT_SAMPLES / 2 + TILE_APRON_SAMPLES - 0.5,
  );
  const rCentre = radiusM + hCentre * exag;
  const ox = bounds.centre[0] * rCentre;
  const oy = bounds.centre[1] * rCentre;
  const oz = bounds.centre[2] * rCentre;

  // The inherited albedo's sub-rectangle, folded into the uv attribute. A
  // tile's albedo stores its row 0 at the v0 edge (imageBitmap.ts), so the v
  // half runs with the mesh's own v: t = offsetV + scale * meshV.
  const site = frame.siteAlbedo ?? null;
  // The region orthoimage is one greyscale HiRISE band, so it is luminance
  // DETAIL over whatever colour this ground inherits and not a replacement for
  // it: binding it as the sole map made a leaf inside the footprint read
  // neutral beside the same-level leaf outside it (docs/ATLAS-TERRAIN.md).
  const orthoDetail = site !== null && (frame.orthoTint ?? true);
  const inherit = tile.albedo || (site !== null && !orthoDetail)
    ? null : frame.albedo ?? null;
  // `uv` carries the inherited colour's rectangle and `uvOrtho` the region
  // image's affine, since the two are now sampled together.
  const sm = site && !orthoDetail ? site.uv : null;
  const smDetail = orthoDetail && site ? site.uv : null;
  // The fill's own chain: it has a floor of its own, shallower than the
  // albedo's, so this is not `inherit` and its rectangle is not `uv`'s.
  const artFill = frame.artificialFill ?? null;
  const inheritArt = (tile.artificial ?? null) === null ? frame.artificial ?? null : null;
  const artProvider = artFill === null ? null : tile.artificial ? tile : inheritArt?.from ?? null;

  // The injected lip: a sample at or beyond the tile's OWN edge carries the
  // step, everything inside it does not. Read off the axis tables rather than
  // off the vertex index, so the four gradient samples either side of an edge
  // vertex land on different sides of the step and the normal tilts with it.
  const sU0 = tabU.s[0]!;
  const sU1 = tabU.s[quads]!;
  const sV0 = tabV.s[0]!;
  const sV1 = tabV.s[quads]!;
  const lipMask = rOffEdges === 0 ? 0b1111 : rOffEdges;
  const lipAt = rOff === 0 ? (): number => 0 : (su: number, sv: number): number => (
    ((lipMask & 1) !== 0 && su <= sU0) || ((lipMask & 2) !== 0 && su >= sU1)
      || ((lipMask & 4) !== 0 && sv <= sV0) || ((lipMask & 8) !== 0 && sv >= sV1) ? rOff : 0);

  const ring = skirts ? perimeter(quads) : null;
  const gridVerts = verts * verts;
  const total = gridVerts + (ring ? ring.length : 0);
  const atmoRef = frame.atmo ?? null;
  // 1 on the skirt ring, 0 on the grid - the grid half is already zeroed by
  // Float32Array's own default, so only the ring needs writing.
  const skirtAttr = atmoRef ? new Float32Array(total) : null;
  if (skirtAttr && ring) skirtAttr.fill(1, gridVerts);
  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  const uv = new Float32Array(total * 2);
  const uvOrtho = smDetail ? new Float32Array(total * 2) : null;
  const uvArt = artProvider ? new Float32Array(total * 2) : null;
  // Own-tile fraction, always - never the ancestor rectangle `uv` reads when
  // this tile inherits its colour: the claim texture is never borrowed, so its
  // uv is unconditionally (i/quads, j/quads), same as `uv` when this tile has
  // no ancestor to point into.
  const uvClaim = tile.claims ? new Float32Array(total * 2) : null;
  let hMin = Infinity;
  let hMax = -Infinity;
  for (let j = 0; j < verts; j++) {
    const sv = tabV.s[j]!;
    const tv = tabV.tan[j]!;
    for (let i = 0; i < verts; i++) {
      const su = tabU.s[i]!;
      const tu = tabU.tan[i]!;
      const h = sampleH(su, sv);
      if (h < hMin) hMin = h;
      if (h > hMax) hMax = h;
      put(0, tu, tv, h, lipAt(su, sv));
      put(3, tabU.tanLo[i]!, tv, sampleH(su - 0.5, sv), lipAt(su - 0.5, sv));
      put(6, tabU.tanHi[i]!, tv, sampleH(su + 0.5, sv), lipAt(su + 0.5, sv));
      put(9, tu, tabV.tanLo[j]!, sampleH(su, sv - 0.5), lipAt(su, sv - 0.5));
      put(12, tu, tabV.tanHi[j]!, sampleH(su, sv + 0.5), lipAt(su, sv + 0.5));
      const ux = pt[6]! - pt[3]!;
      const uy = pt[7]! - pt[4]!;
      const uz = pt[8]! - pt[5]!;
      const vx = pt[12]! - pt[9]!;
      const vy = pt[13]! - pt[10]!;
      const vz = pt[14]! - pt[11]!;
      let nx = uy * vz - uz * vy;
      let ny = uz * vx - ux * vz;
      let nz = ux * vy - uy * vx;
      if (nx * pt[0]! + ny * pt[1]! + nz * pt[2]! < 0) { nx = -nx; ny = -ny; nz = -nz; }
      const nl = Math.hypot(nx, ny, nz) || 1;
      const o3 = (j * verts + i) * 3;
      // Body-fixed to Atlas axes, positions and normals alike: (x, z, -y).
      pos[o3] = pt[0]! - ox;
      pos[o3 + 1] = pt[2]! - oz;
      pos[o3 + 2] = -(pt[1]! - oy);
      nrm[o3] = nx / nl;
      nrm[o3 + 1] = nz / nl;
      nrm[o3 + 2] = -ny / nl;
      const o2 = (j * verts + i) * 2;
      if (sm) {
        const a = i / quads;
        const b = j / quads;
        uv[o2] = sm[0] + sm[1] * a + sm[2] * b;
        uv[o2 + 1] = sm[3] + sm[4] * a + sm[5] * b;
      } else {
        const own = tileAlbedoUv(i / quads, j / quads, inherit);
        uv[o2] = own[0];
        uv[o2 + 1] = own[1];
      }
      if (uvOrtho && smDetail) {
        const a = i / quads;
        const b = j / quads;
        uvOrtho[o2] = smDetail[0] + smDetail[1] * a + smDetail[2] * b;
        uvOrtho[o2 + 1] = smDetail[3] + smDetail[4] * a + smDetail[5] * b;
      }
      if (uvArt) {
        const own = tileAlbedoUv(i / quads, j / quads, inheritArt);
        uvArt[o2] = own[0];
        uvArt[o2 + 1] = own[1];
      }
      if (uvClaim) {
        uvClaim[o2] = i / quads;
        uvClaim[o2 + 1] = j / quads;
      }
    }
  }

  // Procedural relief on a lattice one vertex wider than the grid each way, so
  // every grid vertex's normal is a central difference with no one-sided edge.
  const reliefField = frame.tileRelief ?? null;
  let reliefDelta: Float32Array | null = null;
  let reliefNormal: Float32Array | null = null;
  let reliefLo = 0;
  let reliefHi = 0;
  let reliefEvalMs = 0;
  if (reliefField) {
    const started = performance.now();
    reliefDelta = new Float32Array(total * 3);
    reliefNormal = new Float32Array(total * 3);
    const { n, dirs, cellM } = tileReliefLattice(bounds, radiusM, quads);
    const given = reliefField.heights?.length === n * n ? reliefField.heights : null;
    const lat = new Float64Array(n * n * 3);
    reliefLo = Infinity;
    reliefHi = -Infinity;
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        const k3 = (j * n + i) * 3;
        const x = dirs[k3]!;
        const y = dirs[k3 + 1]!;
        const z = dirs[k3 + 2]!;
        const raw = given ? given[j * n + i]! : reliefField.heightM(x, y, z, cellM);
        const h = Number.isFinite(raw) ? raw * exag : 0;
        lat[k3] = x * (radiusM + h);
        lat[k3 + 1] = y * (radiusM + h);
        lat[k3 + 2] = z * (radiusM + h);
        if (i >= 1 && i <= verts && j >= 1 && j <= verts) {
          if (h < reliefLo) reliefLo = h;
          if (h > reliefHi) reliefHi = h;
          const o3 = ((j - 1) * verts + (i - 1)) * 3;
          reliefDelta[o3] = x * h;
          reliefDelta[o3 + 1] = z * h;
          reliefDelta[o3 + 2] = -y * h;
        }
      }
    }
    for (let j = 1; j <= verts; j++) {
      for (let i = 1; i <= verts; i++) {
        const e = (j * n + i + 1) * 3;
        const w = (j * n + i - 1) * 3;
        const s = ((j - 1) * n + i) * 3;
        const nn = ((j + 1) * n + i) * 3;
        const ux = lat[e]! - lat[w]!;
        const uy = lat[e + 1]! - lat[w + 1]!;
        const uz = lat[e + 2]! - lat[w + 2]!;
        const vx = lat[nn]! - lat[s]!;
        const vy = lat[nn + 1]! - lat[s + 1]!;
        const vz = lat[nn + 2]! - lat[s + 2]!;
        let nx = uy * vz - uz * vy;
        let ny = uz * vx - ux * vz;
        let nz = ux * vy - uy * vx;
        const d = (j * n + i) * 3;
        const dx = dirs[d]!;
        const dy = dirs[d + 1]!;
        const dz = dirs[d + 2]!;
        if (nx * dx + ny * dy + nz * dz < 0) { nx = -nx; ny = -ny; nz = -nz; }
        const rl = Math.hypot(nx, ny, nz) || 1;
        // The same stencil over the bare sphere: an equal-angle lattice's chords
        // are not tangent off the face centre lines, and that lean is not relief.
        const sux = dirs[e]! - dirs[w]!;
        const suy = dirs[e + 1]! - dirs[w + 1]!;
        const suz = dirs[e + 2]! - dirs[w + 2]!;
        const svx = dirs[nn]! - dirs[s]!;
        const svy = dirs[nn + 1]! - dirs[s + 1]!;
        const svz = dirs[nn + 2]! - dirs[s + 2]!;
        let sx = suy * svz - suz * svy;
        let sy = suz * svx - sux * svz;
        let sz = sux * svy - suy * svx;
        if (sx * dx + sy * dy + sz * dz < 0) { sx = -sx; sy = -sy; sz = -sz; }
        const sl = Math.hypot(sx, sy, sz) || 1;
        // The relief's tilt, added to the tile's own normal.
        const o3 = ((j - 1) * verts + (i - 1)) * 3;
        const bx = nrm[o3]! + nx / rl - sx / sl;
        const by = nrm[o3 + 1]! + nz / rl - sz / sl;
        const bz = nrm[o3 + 2]! - ny / rl + sy / sl;
        const bl = Math.hypot(bx, by, bz) || 1;
        reliefNormal[o3] = bx / bl;
        reliefNormal[o3 + 1] = by / bl;
        reliefNormal[o3 + 2] = bz / bl;
      }
    }
    reliefEvalMs = performance.now() - started;
  }

  // The geomorph: where each vertex sits on the PARENT's vertex lattice, which
  // over this tile's extent is exactly the even indices. An odd vertex is the
  // midpoint of the two even ones beside it, in three dimensions rather than in
  // height alone, because the parent's triangles are chords of the sphere.
  let morphDelta: Float32Array | null = null;
  let reliefMorph: Float32Array | null = null;
  if (geomorph) {
    morphDelta = new Float32Array(total * 3);
    reliefMorph = reliefDelta ? new Float32Array(total * 3) : null;
    for (let j = 0; j < verts; j++) {
      const j0 = j - (j & 1);
      const j1 = j + (j & 1);
      for (let i = 0; i < verts; i++) {
        const i0 = i - (i & 1);
        const i1 = i + (i & 1);
        const o3 = (j * verts + i) * 3;
        for (let c = 0; c < 3; c++) {
          const coarse = 0.25 * (pos[(j0 * verts + i0) * 3 + c]! + pos[(j0 * verts + i1) * 3 + c]!
            + pos[(j1 * verts + i0) * 3 + c]! + pos[(j1 * verts + i1) * 3 + c]!);
          morphDelta[o3 + c] = coarse - pos[o3 + c]!;
          if (reliefMorph && reliefDelta) {
            reliefMorph[o3 + c] = 0.25 * (reliefDelta[(j0 * verts + i0) * 3 + c]!
              + reliefDelta[(j0 * verts + i1) * 3 + c]! + reliefDelta[(j1 * verts + i0) * 3 + c]!
              + reliefDelta[(j1 * verts + i1) * 3 + c]!) - reliefDelta[o3 + c]!;
          }
        }
      }
    }
  }

  if (ring) {
    // Radially inward from the tile's own edge, deep enough to cover how far
    // the coarser neighbour's surface can be from this one. Normal, uv and
    // morph are the ring vertex's, so the curtain shades and moves with the
    // ground it hangs off rather than reading as a wall of its own.
    const relief = Number.isFinite(hMax - hMin) ? hMax - hMin : 0;
    // Both terms are heights, so both are exaggerated with the ground: the
    // crack the curtain covers is as deep as the relief the slider stretched.
    const skirtM = Math.max(SKIRT_FLOOR_SAMPLES * gsd(tile.level, radiusM),
      SKIRT_RELIEF_FRACTION * relief) * exag + (reliefDelta ? reliefHi - reliefLo : 0);
    const cx = ox;
    const cy = oz;
    const cz = -oy;
    for (let r = 0; r < ring.length; r++) {
      const from = ring[r]! * 3;
      const to = (gridVerts + r) * 3;
      const wx = pos[from]! + cx;
      const wy = pos[from + 1]! + cy;
      const wz = pos[from + 2]! + cz;
      const inv = skirtM / (Math.hypot(wx, wy, wz) || 1);
      pos[to] = pos[from]! - wx * inv;
      pos[to + 1] = pos[from + 1]! - wy * inv;
      pos[to + 2] = pos[from + 2]! - wz * inv;
      nrm[to] = nrm[from]!;
      nrm[to + 1] = nrm[from + 1]!;
      nrm[to + 2] = nrm[from + 2]!;
      uv[(gridVerts + r) * 2] = uv[ring[r]! * 2]!;
      uv[(gridVerts + r) * 2 + 1] = uv[ring[r]! * 2 + 1]!;
      if (uvOrtho) {
        uvOrtho[(gridVerts + r) * 2] = uvOrtho[ring[r]! * 2]!;
        uvOrtho[(gridVerts + r) * 2 + 1] = uvOrtho[ring[r]! * 2 + 1]!;
      }
      if (uvArt) {
        uvArt[(gridVerts + r) * 2] = uvArt[ring[r]! * 2]!;
        uvArt[(gridVerts + r) * 2 + 1] = uvArt[ring[r]! * 2 + 1]!;
      }
      if (morphDelta) {
        morphDelta[to] = morphDelta[from]!;
        morphDelta[to + 1] = morphDelta[from + 1]!;
        morphDelta[to + 2] = morphDelta[from + 2]!;
      }
      for (const a of [reliefDelta, reliefMorph, reliefNormal]) {
        if (!a) continue;
        a[to] = a[from]!;
        a[to + 1] = a[from + 1]!;
        a[to + 2] = a[from + 2]!;
      }
    }
  }

  if (import.meta.env.DEV) {
    // Two implementations of one projection: `put` inlines what
    // `directionForFaceUv` does. Comparing the centre direction is what says
    // they still agree.
    put(0, faceTangent((bounds.u0 + bounds.u1) / 2), faceTangent((bounds.v0 + bounds.v1) / 2), 0);
    const dx = pt[0]! / radiusM - bounds.centre[0];
    const dy = pt[1]! / radiusM - bounds.centre[1];
    const dz = pt[2]! / radiusM - bounds.centre[2];
    if (Math.hypot(dx, dy, dz) > 1e-12) {
      console.error(`tileMesh ${formatTileKey(tile.key)}: the mesh projection and `
        + `directionForFaceUv disagree by ${Math.hypot(dx, dy, dz)}`);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  if (uvOrtho) geometry.setAttribute('uvOrtho', new THREE.BufferAttribute(uvOrtho, 2));
  if (uvArt) geometry.setAttribute('uvArt', new THREE.BufferAttribute(uvArt, 2));
  if (uvClaim) geometry.setAttribute('uvClaim', new THREE.BufferAttribute(uvClaim, 2));
  if (morphDelta) geometry.setAttribute('morphDelta', new THREE.BufferAttribute(morphDelta, 3));
  if (skirtAttr) geometry.setAttribute('skirt', new THREE.BufferAttribute(skirtAttr, 1));
  if (reliefDelta) geometry.setAttribute('reliefDelta', new THREE.BufferAttribute(reliefDelta, 3));
  if (reliefMorph) geometry.setAttribute('reliefMorph', new THREE.BufferAttribute(reliefMorph, 3));
  if (reliefNormal) geometry.setAttribute('reliefNormal', new THREE.BufferAttribute(reliefNormal, 3));
  geometry.setIndex(sharedIndex(quads, windingFlipped(face), ring !== null));

  // The tile's own imagery, or the ancestor's it was handed. Either way the
  // texture comes out of the shared cache, since the tile drawing its own
  // albedo and the descendants borrowing it are the same GL texture.
  const cache = frame.textures ?? null;
  const provider = tile.albedo ? tile : inherit?.from ?? null;
  // The site image is owned by whoever loaded it and outlives every mesh
  // drawing out of it, so it is not ref-counted through the tile cache.
  const transfer = frame.albedoTransfer ?? null;
  const cached = provider && cache ? cache.acquire(provider, frame.anisotropy ?? 1, 'albedo', transfer) : null;
  const map = site && !orthoDetail ? site.map : cached;
  // Only the body's own imagery is linear; a site image over it is colour.
  const albedoDnU = albedoTransferUniform(transfer, map !== null && map === cached);
  const mapId = cached && provider ? formatTileKey(provider.key) : null;
  // Detail over nothing: the ground under this leaf has no ancestor colour, so
  // the orthoimage carries the whole albedo through the plot's own tint curve
  // rather than reading as bare grey.
  const orthoCurve = orthoDetail && map === null;
  // The globe's own material (`createOrrery`), so a tile and the coarse picture
  // it lands on top of cannot disagree about how Mars is lit.
  const material = new THREE.MeshStandardMaterial({
    roughness: GROUND_ROUGHNESS,
    metalness: GROUND_METALNESS,
    ...(map ? { map }
      : orthoCurve ? { color: new THREE.Color(0xffffff) }
        : { color: new THREE.Color(frame.noImageryColor ?? NO_IMAGERY_COLOR) }),
  });
  const morphU = { value: geomorph ? clamp01(frame.morph ?? 1) : 1 };
  const orthoU = { value: orthoDetail && site ? site.map : null };
  const orthoLowU = { value: orthoDetail && site ? site.lowPass : null };
  // Own texture, own GPU upload, every rebuild: never borrowed from an
  // ancestor and never shared through `frame.textures`, so it is not worth
  // the cache's ref-counting - it is freed below with the rest of this mesh.
  const claimTex = tile.claims
    ? new THREE.DataTexture(
      tile.claims.data, TILE_CLAIM_TEXELS, TILE_CLAIM_TEXELS, THREE.RedFormat,
    )
    : null;
  if (claimTex) {
    claimTex.name = `tile-claims:${formatTileKey(tile.key)}`;
    claimTex.wrapS = claimTex.wrapT = THREE.ClampToEdgeWrapping;
    claimTex.minFilter = claimTex.magFilter = THREE.LinearFilter;
    claimTex.generateMipmaps = false;
    claimTex.needsUpdate = true;
  }
  const claimU = { value: claimTex as THREE.Texture | null };
  // Ref-counted through the same cache the albedo is, under its own id: a
  // descendant borrowing an L3 fill and the L3 tile itself are one GL texture.
  const artTex = artProvider && cache
    ? cache.acquire(artProvider, frame.anisotropy ?? 1, 'artificial') : null;
  const artId = artTex && artProvider ? tileImageId(artProvider.key, 'artificial') : null;
  const artU = { value: artTex };
  // No ancestor of this tile ever streamed a fill: the body's own globe map
  // stands in, sampled by direction rather than by this tile's uv, so a
  // bucket that predates the artificial role still draws something over
  // invented ground instead of the bake's flat colour.
  const globeArtFill = artTex === null && frame.globeArtificial
    ? { ref: frame.globeArtificial, origin: bodyToAtlas(ox, oy, oz, new THREE.Vector3()) }
    : null;
  // The globe's relief on this tile, sampled by direction off the same centre
  // the fill fallback reads from. Unconditional where the body publishes one:
  // the map's own alpha is 0 over every texel an instrument measured, so the
  // chunk is the identity on a tile that needs none of it.
  const globeRelief = frame.globeRelief
    ? { ref: frame.globeRelief, origin: bodyToAtlas(ox, oy, oz, new THREE.Vector3()) }
    : null;
  applyTileChunks(material, morphU, morphDelta !== null, atmoRef,
    orthoDetail ? { map: orthoU, lowPass: orthoLowU, curve: orthoCurve } : null,
    claimTex ? { map: claimU } : null,
    frame.coverage ?? null,
    artTex && artFill ? { map: artU, on: artFill.on, force: artFill.force, dn: artFill.dn } : null,
    globeArtFill, globeRelief, reliefField && reliefDelta ? { on: reliefField.on } : null, albedoDnU,
    frame.bodyShadow ? { ref: frame.bodyShadow, origin: bodyToAtlas(ox, oy, oz, new THREE.Vector3()) } : null);

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = formatTileKey(tile.key);
  mesh.userData['tileLevel'] = tile.level;
  // Which imagery products this mesh actually samples, for the instrument that
  // reads a colour step across a boundary between two of them.
  mesh.userData['tileImagery'] = site
    ? (inherit ? 'site+inherited' : 'site')
    : tile.albedo ? 'own' : inherit ? 'inherited' : 'none';
  bodyToAtlas(ox, oy, oz, mesh.position);
  // The live geomorph, for an instrument reading the surface this mesh DRAWS
  // off its own buffers: the uniform itself is inside the injected chunk.
  mesh.userData['tileMorph'] = morphU.value;

  let live = true;
  return {
    object: mesh,
    triangles: quads * quads * 2 + (ring ? ring.length * 2 : 0),
    textured: map !== null || orthoDetail,
    // The provider REQUESTED, not the texture acquired: src/atlas.ts compares
    // this against what the traversal asked for, and a null here against a
    // non-null there queues a retarget every pass for as long as the tile is
    // drawn.
    // Both providers when both are sampled, because the inherited one can go
    // away under a drawn tile and that is a rebuild, not a no-op.
    albedoFromId: site
      ? (inherit ? `${site.id}|${formatTileKey(inherit.from.key)}` : site.id)
      : inherit ? formatTileKey(inherit.from.key) : null,
    // The provider REQUESTED, the same way the albedo one is: an ancestor
    // carrying the fill can go away under a drawn tile, and that is a rebuild.
    artificialFromId: inheritArt ? formatTileKey(inheritArt.from.key) : null,
    artificialTextured: artTex !== null,
    artificialGlobeTextured: globeArtFill !== null,
    reliefShaded: globeRelief !== null,
    heightFromId: inheritH ? formatTileKey(inheritH.from.key) : null,
    heightRangeM: Number.isFinite(hMax - hMin) ? +(hMax - hMin).toFixed(3) : 0,
    skirted: ring !== null,
    reliefDisplaced: reliefDelta !== null,
    reliefEvalMs,
    reliefLoM: reliefDelta ? reliefLo : 0,
    reliefHiM: reliefDelta ? reliefHi : 0,
    setMorph(t: number): void {
      morphU.value = geomorph ? clamp01(t) : 1;
      mesh.userData['tileMorph'] = morphU.value;
    },
    drawnRadiusAt(dir: Vec3): number {
      const f = faceUvForDirection(dir);
      if (f.face !== bounds.face) return NaN;
      const fu = ((f.u - bounds.u0) / (bounds.u1 - bounds.u0)) * quads;
      const fv = ((f.v - bounds.v0) / (bounds.v1 - bounds.v0)) * quads;
      if (!(fu >= 0 && fu <= quads && fv >= 0 && fv <= quads)) return NaN;
      const iu = Math.min(quads - 1, Math.floor(fu));
      const iv = Math.min(quads - 1, Math.floor(fv));
      const back = morphDelta ? 1 - morphU.value : 0;
      const on = reliefField && reliefDelta ? reliefField.on.value : 0;
      const at = (i: number, j: number, k: number): void => {
        const o3 = (j * verts + i) * 3;
        let dx = morphDelta ? morphDelta[o3]! * back : 0;
        let dy = morphDelta ? morphDelta[o3 + 1]! * back : 0;
        let dz = morphDelta ? morphDelta[o3 + 2]! * back : 0;
        // The vertex chunk's displacement, including its `on > 0` gate.
        if (on > 0 && reliefDelta) {
          dx += on * (reliefDelta[o3]! + (reliefMorph ? reliefMorph[o3]! * back : 0));
          dy += on * (reliefDelta[o3 + 1]! + (reliefMorph ? reliefMorph[o3 + 1]! * back : 0));
          dz += on * (reliefDelta[o3 + 2]! + (reliefMorph ? reliefMorph[o3 + 2]! * back : 0));
        }
        // Atlas axes back to body-fixed, the inverse of what `put` wrote.
        drawnTri[k] = pos[o3]! + dx + ox;
        drawnTri[k + 1] = oy - (pos[o3 + 2]! + dz);
        drawnTri[k + 2] = pos[o3 + 1]! + dy + oz;
      };
      // The triangle `sharedIndex` draws: its diagonal runs u1/v0 to u0/v1,
      // and the ray is on u0/v0's side of the plane through it and the centre
      // or on u1/v1's.
      at(iu, iv, 0);
      at(iu + 1, iv, 3);
      at(iu, iv + 1, 6);
      const t = drawnTri;
      const mx = t[4]! * t[8]! - t[5]! * t[7]!;
      const my = t[5]! * t[6]! - t[3]! * t[8]!;
      const mz = t[3]! * t[7]! - t[4]! * t[6]!;
      const sideA = mx * t[0]! + my * t[1]! + mz * t[2]!;
      const sideDir = mx * dir[0] + my * dir[1] + mz * dir[2];
      if (sideA * sideDir < 0) at(iu + 1, iv + 1, 0);
      // Where the ray meets that triangle's PLANE, not its three radii blended:
      // a triangle is a chord under them, 504 m deep mid-quad at L1 on Mars.
      const e1x = t[3]! - t[0]!;
      const e1y = t[4]! - t[1]!;
      const e1z = t[5]! - t[2]!;
      const e2x = t[6]! - t[0]!;
      const e2y = t[7]! - t[1]!;
      const e2z = t[8]! - t[2]!;
      const nx = e1y * e2z - e1z * e2y;
      const ny = e1z * e2x - e1x * e2z;
      const nz = e1x * e2y - e1y * e2x;
      const nDir = nx * dir[0] + ny * dir[1] + nz * dir[2];
      if (nDir === 0) return NaN;
      return ((nx * t[0]! + ny * t[1]! + nz * t[2]!) / nDir)
        * Math.hypot(dir[0], dir[1], dir[2]);
    },
    retintClaims(claimedAt: (res: number) => ReadonlySet<string>): boolean {
      const c = tile.claims;
      if (!c || !claimTex) return false;
      if (!retintClaimTexture(c, claimedAt(c.res))) return false;
      claimTex.needsUpdate = true;
      return true;
    },
    edgeVerts(edge: number): Float32Array {
      const out = new Float32Array(verts * 3);
      for (let k = 0; k < verts; k++) {
        const i = edge === 0 ? 0 : edge === 1 ? quads : k;
        const j = edge === 0 || edge === 1 ? k : edge === 2 ? 0 : quads;
        const o3 = (j * verts + i) * 3;
        out[k * 3] = pos[o3]!;
        out[k * 3 + 1] = pos[o3 + 1]!;
        out[k * 3 + 2] = pos[o3 + 2]!;
      }
      return out;
    },
    edgeEnds(edge: number): readonly [THREE.Vector3, THREE.Vector3] {
      const last = quads;
      const at = (i: number, j: number): THREE.Vector3 => {
        const o3 = (j * verts + i) * 3;
        return new THREE.Vector3(pos[o3], pos[o3 + 1], pos[o3 + 2]);
      };
      switch (edge) {
        case 0: return [at(0, 0), at(0, last)];
        case 1: return [at(last, 0), at(last, last)];
        case 2: return [at(0, 0), at(last, 0)];
        default: return [at(0, last), at(last, last)];
      }
    },
    warm(renderer: THREE.WebGLRenderer): void {
      if (!live) return;
      if (map) renderer.initTexture(map);
      if (artTex) renderer.initTexture(artTex);
      if (claimTex) renderer.initTexture(claimTex);
      if (orthoU.value) renderer.initTexture(orthoU.value);
      if (orthoLowU.value) renderer.initTexture(orthoLowU.value);
    },
    dispose(): void {
      if (!live) return;
      live = false;
      geometry.dispose();
      material.dispose();
      if (mapId && cache) cache.release(mapId);
      if (artId && cache) cache.release(artId);
      claimTex?.dispose();
    },
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
